import { useState } from "react";
import styled from "styled-components";
import { useGlobalContext } from "../contexts/Constants";
import { ProfileForm } from "../components/ProfileForm";
import { SettingsForm } from "../components/SettingsForm";

import axios, { AxiosError } from "axios";

const Container = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
  color: ${(props) => props.theme.secondary};
`;

const Subtitle = styled.p`
  color: ${(props) => props.theme.tertiary};
`;

const TabContainer = styled.div`
  background-color: ${(props) => props.theme.secondaryGrey};
  border-radius: 0.5rem;
  padding: 0.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const TabButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 1rem;
  border: none;
  background: ${(props) => (props.$active ? props.theme.white : "transparent")};
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${(props) =>
    props.$active ? props.theme.secondary : props.theme.tertiary};
  width: auto;
  text-align: left;

  @media (max-width: 767px) {
    width: 100%;
    text-align: center;
  }
  &:hover {
    background: ${(props) =>
      props.$active ? props.theme.white : "rgba(255, 255, 255, 0.5)"};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${(props) => props.theme.secondary};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.secondaryGrey};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.secondary};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary};
  }
`;

const ResponseMessage = styled.div<{ msg: string }>`
  color: ${({ msg }) => (msg === "success" ? "green" : "#842029")};
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background-color: ${(props) => props.theme.primary};
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  color: ${(props) => props.theme.white};
  font-weight: 500;
  margin-top: 0.5rem;

  &:hover {
    opacity: 0.9;
  }
`;

const LinkedAccountsList = styled.div`
  margin-top: 1rem;
`;

const LinkedAccountItem = styled.div`
  padding: 1rem;
  background: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.secondaryGrey};
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const AccountName = styled.div`
  font-weight: 500;
  color: ${(props) => props.theme.secondary};
`;

const AccountEmail = styled.div`
  color: ${(props) => props.theme.tertiary};
  font-size: 0.875rem;
`;

const Status = styled.div<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  background: ${(props) =>
    props.status === "pending"
      ? "#FFF3CD"
      : props.status === "accepted"
      ? "#D1E7DD"
      : "#F8D7DA"};
  color: ${(props) =>
    props.status === "pending"
      ? "#856404"
      : props.status === "accepted"
      ? "#0F5132"
      : "#842029"};
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${(props) => props.theme.white};
  background-color: ${(props) => props.theme.primary};
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }
`;

const PageAccount = () => {
  const {
    loggedIn,
    linkedUsers,
    getLinks,
    userData,
    getUserData,
    setSelectedPageState,
  } = useGlobalContext();
  const [activeTab, setActiveTab] = useState("profile");

  const [profileForm, setProfileForm] = useState({
    name: userData.name,
    email: userData.email,
    business: userData.business || "",
  });

  const [settingsForm, setSettingsForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [responseMessage, setResponseMessage] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [profileError, setProfileError] = useState("");
  const [settingsError, setSettingsError] = useState("");
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError("");
    try {
      await axios.patch(
        "/api/user",
        {
          ...profileForm,
        },
        {
          headers: {
            Authorization: `Bearer ${loggedIn.token}`,
          },
        }
      );
      getUserData();
      setProfileError("");
      setSelectedPageState("viewAll");
    } catch (e) {
      if (e instanceof AxiosError) {
        setProfileError(e.response?.data.message || "An error occurred");
      } else {
        setProfileError("An unexpected error occurred");
      }
    }
  };

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsError("");

    if (settingsForm.newPassword !== settingsForm.confirmPassword) {
      setSettingsError("New passwords do not match");
      return;
    }

    if (settingsForm.newPassword.length < 6) {
      setSettingsError("New password must be at least 6 characters long");
      return;
    }

    try {
      await axios.patch(
        "/api/user/password",
        {
          ...settingsForm,
        },
        {
          headers: { Authorization: `Bearer ${loggedIn.token}` },
        }
      );
      setSelectedPageState("viewAll");
      setSettingsForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (e) {
      if (e instanceof AxiosError) {
        setSettingsError(e.response?.data.message || "An error occurred");
      } else {
        setSettingsError("An unexpected error occurred");
      }
    }
  };

  const handleLinkAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setResponseMessage("");
    try {
      await axios.post(
        "/api/link_user",
        {
          linkee_email: linkEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${loggedIn.token}`,
          },
        }
      );
      setLinkEmail("");
      setResponseMessage("success");
      getLinks();
    } catch (e) {
      const error = e as AxiosError;
      setResponseMessage((error.response?.data as { error: string }).error);
      console.log(error);
    }
  };

  const handleAccept = async (email: string) => {
    try {
      await axios.post(
        "/api/link_response",
        {
          email,
          decision: "accepted",
        },
        {
          headers: {
            Authorization: `Bearer ${loggedIn.token}`,
          },
        }
      );
      getLinks();
    } catch (e) {
      console.error(e);
    }
  };

  const handleReject = async (email: string) => {
    try {
      await axios.post(
        "/api/link_response",
        {
          email,
          decision: "rejected",
        },
        {
          headers: {
            Authorization: `Bearer ${loggedIn.token}`,
          },
        }
      );
      getLinks();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Account Settings</Title>
        <Subtitle>Manage your account details and preferences</Subtitle>
      </Header>{" "}
      <TabContainer>
        <TabButton
          $active={activeTab === "profile"}
          onClick={() => {
            setActiveTab("profile");
            setProfileError("");
            setSettingsError("");
          }}
        >
          Profile
        </TabButton>
        <TabButton
          $active={activeTab === "settings"}
          onClick={() => {
            setActiveTab("settings");
            setProfileError("");
            setSettingsError("");
          }}
        >
          Settings
        </TabButton>
        <TabButton
          $active={activeTab === "linked"}
          onClick={() => {
            setActiveTab("linked");
            setProfileError("");
            setSettingsError("");
          }}
        >
          Linked Accounts
        </TabButton>
      </TabContainer>
      {activeTab === "profile" && (
        <ProfileForm
          profileForm={profileForm}
          setProfileForm={setProfileForm}
          onSubmit={handleProfileSubmit}
          error={profileError}
        />
      )}
      {activeTab === "settings" && (
        <SettingsForm
          settingsForm={settingsForm}
          setSettingsForm={setSettingsForm}
          onSubmit={handleSettingsSubmit}
          error={settingsError}
        />
      )}
      {activeTab === "linked" && (
        <>
          <form onSubmit={handleLinkAccount}>
            <FormGroup>
              <Label>Link Account</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
              />
            </FormGroup>
            <ResponseMessage msg={responseMessage}>
              {responseMessage === "success"
                ? "User successfully linked"
                : responseMessage}
            </ResponseMessage>
            <SaveButton type="submit">Send Link Request</SaveButton>
          </form>

          <LinkedAccountsList>
            {linkedUsers.map((account) => (
              <LinkedAccountItem key={account.email}>
                <AccountInfo>
                  <AccountName>{account.name}</AccountName>
                  <AccountEmail>{account.email}</AccountEmail>
                </AccountInfo>
                <ButtonContainer>
                  <Status status={account.status}>
                    {account.status.charAt(0).toUpperCase() +
                      account.status.slice(1)}
                  </Status>
                  {account.status === "pending" && (
                    <>
                      <ActionButton onClick={() => handleAccept(account.email)}>
                        Accept
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleReject(account.email)}
                        style={{ backgroundColor: "#dc3545" }}
                      >
                        Reject
                      </ActionButton>
                    </>
                  )}
                </ButtonContainer>
              </LinkedAccountItem>
            ))}
          </LinkedAccountsList>
        </>
      )}
    </Container>
  );
};

export default PageAccount;
